// modules/reporteCierre/pages/ReporteCierrePage.jsx
// Migracion del modulo "Cierre" (reporte_cierre/index.php + busqueda.php +
// guarda.php): totales facturados del mes por sucursal, con la posibilidad
// de "cerrar" el mes (grabar una foto permanente en factura_cierre) y un
// historial de los meses ya cerrados en el año.
//
// Se le arreglaron tres cosas del reporte legado:
//   - Unia factura con cliente_servicio/servicio/cliente/informacion_legal
//     sin usar ningun campo de esas tablas para el total, y el join a
//     informacion_legal era INNER — como 347 de 3591 clientes no tienen fila
//     ahi, esas facturas se perdian del calculo (verificado en vivo: un mes
//     real bajaba de 673 a 671 facturas). Se saca el join entero.
//   - En el historial por año, las columnas de Cochabamba estaban
//     invertidas (Bs. mostraba el valor en dolares y viceversa).
//   - "Cerrar mes" confiaba en los montos que mandaba el navegador por GET
//     (se podian manipular) y no impedia cerrar el mismo mes dos veces. Ahora
//     el backend recalcula los totales el mismo y rechaza un cierre
//     duplicado.
//
// El permiso para cerrar el mes (antes hardcodeado al usuario "adri") pasa a
// los roles admin/gerente/conta.
import React, { useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { getResumenMes, getHistorialAnio, cerrarMes } from "../services/reporteCierreService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const bodySx = { "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } };

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const MESES = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const NOMBRES_MES = Object.fromEntries(MESES.map((m) => [m.value, m.label]));

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

const ROLES_PUEDEN_CERRAR = ["admin", "gerente", "conta"];

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFechaHora = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ReporteCierrePage = () => {
  const { user } = useAuth();
  const puedeCerrar = ROLES_PUEDEN_CERRAR.includes(user?.role);
  const [tab, setTab] = useState(0);

  // --- Tab 0: Cierre del Mes ---
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(anioActual);
  const [resumen, setResumen] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [errorCierre, setErrorCierre] = useState("");

  const buscarResumen = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getResumenMes({ year, mes });
      setResumen(data);
      setBuscado(true);
    } catch (e) {
      setResumen(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const confirmarCierre = async () => {
    setCerrando(true);
    setErrorCierre("");
    try {
      await cerrarMes({ year, mes });
      setDialogoAbierto(false);
      await buscarResumen();
    } catch (e) {
      setErrorCierre(e?.response?.data?.error || "No se pudo cerrar el mes.");
    } finally {
      setCerrando(false);
    }
  };

  // --- Tab 1: Historial ---
  const [yearHistorial, setYearHistorial] = useState(anioActual);
  const [historial, setHistorial] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState("");
  const [buscadoHistorial, setBuscadoHistorial] = useState(false);

  const buscarHistorial = async () => {
    setLoadingHistorial(true);
    setErrorHistorial("");
    try {
      const { data } = await getHistorialAnio({ year: yearHistorial });
      setHistorial(data);
      setBuscadoHistorial(true);
    } catch (e) {
      setHistorial(null);
      setErrorHistorial(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoadingHistorial(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Cierre de Facturación"
        subtitle="Totales facturados por mes y sucursal, y cierre mensual"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab label="Cierre del Mes" />
            <Tab label="Historial" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                select size="small" label="Mes" sx={{ ...compactFieldSx, width: 170 }}
                value={mes} onChange={(e) => setMes(Number(e.target.value))}
              >
                {MESES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }}
                value={year} onChange={(e) => setYear(Number(e.target.value))}
              >
                {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
              </TextField>
              <Button
                size="small" variant="contained"
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={buscarResumen} disabled={loading} sx={{ minHeight: 32 }}
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!buscado ? (
              <Alert severity="info">Elige mes y año, y presiona Buscar.</Alert>
            ) : (
              <>
                {resumen.yaCerrado && (
                  <Alert severity="success" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                    Este mes ya fue cerrado{resumen.cierre?.fecha ? ` el ${formatFechaHora(resumen.cierre.fecha)}` : ""}
                    {resumen.cierre?.detalle ? ` (${resumen.cierre.detalle})` : ""}.
                    Los montos abajo son los totales actuales en el sistema; los que quedaron grabados al cerrar
                    pueden diferir si hubo cambios posteriores.
                  </Alert>
                )}

                <Paper variant="outlined">
                  <TableContainer>
                    <Table size="small" sx={bodySx}>
                      <TableHead>
                        <TableRow>
                          <StyledHeaderCell>Sucursal</StyledHeaderCell>
                          <StyledHeaderCell align="right">Monto Bs</StyledHeaderCell>
                          <StyledHeaderCell align="right">Monto $us</StyledHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {SUCURSALES.map((s) => (
                          <TableRow key={s} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{s}</TableCell>
                            <TableCell align="right">{formatMoneda(resumen.porSucursal[s]?.bolivianos)}</TableCell>
                            <TableCell align="right">{formatMoneda(resumen.porSucursal[s]?.dolares)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(resumen.totales.bolivianos)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(resumen.totales.dolares)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  {resumen.yaCerrado ? (
                    <Chip icon={<LockIcon fontSize="small" />} label="Mes Cerrado" color="default" variant="outlined" />
                  ) : puedeCerrar ? (
                    <Button variant="contained" color="warning" startIcon={<LockIcon fontSize="small" />} onClick={() => setDialogoAbierto(true)}>
                      Cerrar Mes
                    </Button>
                  ) : null}
                </Box>
              </>
            )}

            <Dialog open={dialogoAbierto} onClose={() => !cerrando && setDialogoAbierto(false)}>
              <DialogTitle>¿Cerrar {NOMBRES_MES[mes]} {year}?</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Se va a grabar como definitivo el total facturado de este mes por sucursal. Esta acción no se puede deshacer.
                </Typography>
                {errorCierre && <Alert severity="error" sx={{ mt: 1 }}>{errorCierre}</Alert>}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogoAbierto(false)} disabled={cerrando}>Cancelar</Button>
                <Button
                  variant="contained" color="warning" onClick={confirmarCierre} disabled={cerrando}
                  startIcon={cerrando ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {cerrando ? "Cerrando..." : "Confirmar Cierre"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }}
                value={yearHistorial} onChange={(e) => setYearHistorial(Number(e.target.value))}
              >
                {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
              </TextField>
              <Button
                size="small" variant="contained"
                startIcon={loadingHistorial ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={buscarHistorial} disabled={loadingHistorial} sx={{ minHeight: 32 }}
              >
                {loadingHistorial ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {errorHistorial && <Alert severity="error" sx={{ mb: 2 }}>{errorHistorial}</Alert>}

            {!buscadoHistorial ? (
              <Alert severity="info">Elige un año y presiona Buscar.</Alert>
            ) : historial.meses.length === 0 ? (
              <Alert severity="info">No hay meses cerrados en {yearHistorial}.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={bodySx}>
                    <TableHead>
                      <TableRow>
                        <StyledHeaderCell rowSpan={2}>Mes</StyledHeaderCell>
                        {SUCURSALES.map((s) => (
                          <StyledHeaderCell key={s} align="center" colSpan={2}>{s}</StyledHeaderCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        {SUCURSALES.map((s) => (
                          <React.Fragment key={s}>
                            <StyledHeaderCell align="right">Bs.</StyledHeaderCell>
                            <StyledHeaderCell align="right">$us.</StyledHeaderCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historial.meses.map((m) => {
                        const numMes = Number(m.fechaCierre.split("-")[1]);
                        return (
                          <TableRow key={m.fechaCierre} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{NOMBRES_MES[numMes] || m.fechaCierre}</TableCell>
                            {SUCURSALES.map((s) => (
                              <React.Fragment key={s}>
                                <TableCell align="right">{formatMoneda(m.porSucursal[s]?.bolivianos)}</TableCell>
                                <TableCell align="right">{formatMoneda(m.porSucursal[s]?.dolares)}</TableCell>
                              </React.Fragment>
                            ))}
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        {SUCURSALES.map((s) => (
                          <React.Fragment key={s}>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(historial.totalPorSucursal[s]?.bolivianos)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(historial.totalPorSucursal[s]?.dolares)}</TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteCierrePage;
